import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, shareReplay } from 'rxjs';
import { PDFDocument, StandardFonts } from 'pdf-lib';
import { Vibrant } from 'node-vibrant/browser';
import { Palette } from '@vibrant/color';
import { GenerationProgress, ReportConfig } from './interfaces';
import { MixpanelService } from '@core/services/mixpanel.service';
import html2canvas from 'html2canvas';

@Injectable({
  providedIn: 'root',
})
export class ReportGeneratorService {
  private readonly _progress = new BehaviorSubject<GenerationProgress>({
    currentPage: 0,
    totalPages: 0,
    pageTitle: '',
    status: 'idle',
  });

  public progress$ = this._progress.asObservable().pipe(shareReplay(1));

  constructor(private mixpanelService: MixpanelService) {}

  generateReport(config: ReportConfig, pageElements: HTMLElement[]): Observable<Uint8Array> {
    return new Observable<Uint8Array>((observer) => {
      (async () => {
        try {
          this._progress.next({
            currentPage: 0,
            totalPages: 0,
            pageTitle: '',
            status: 'idle',
          });

          // Check for render errors first
          const errorString = this.generateErrorString(config.renderErrors);
          if (errorString && errorString.length > 0) {
            throw Error(errorString);
          }

          const template = config.template!;
          const totalPages = template.pages.length;

          console.log('Generating PDF...');

          this._progress.next({
            currentPage: 1,
            totalPages,
            pageTitle: `Page 1 of ${totalPages}`,
            status: 'processing',
          });

          // Create PDF document
          const pdfDoc = await PDFDocument.create();
          const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
          const pdfRenderOptions: Record<string, any> = {
            fonts: {
              helvetica: helveticaFont,
            },
          };

          for (const [pageIndex, pageData] of template.pages.entries()) {
            const currentPage = pageIndex + 1;

            this._progress.next({
              currentPage,
              totalPages,
              pageTitle: `Page ${currentPage} of ${totalPages}`,
              status: 'processing',
            });

            const page = pdfDoc.addPage(this.getPageDimensions(pageData.orientation || 'portrait'));

            const pageElement = pageElements[pageIndex];
            const canvas = await html2canvas(pageElement, {
              scale: 3,
              backgroundColor: '#ffffff',
              useCORS: true,
              allowTaint: false,
              removeContainer: true,
              onclone: (clonedDoc) => {
                const orgLogoImgs = clonedDoc.querySelectorAll('.org-logo-img');
                orgLogoImgs.forEach((img: Element) => {
                  const htmlImg = img as HTMLImageElement;
                  const div = clonedDoc.createElement('div');
                  div.style.cssText = `
                  width: 100%;
                  height: 100%;
                  background-image: url(${htmlImg.src});
                  background-size: contain;
                  background-repeat: no-repeat;
                  background-position: center;
                `;
                  htmlImg.parentElement?.replaceChild(div, htmlImg);
                });
                const excludeFromPdf = clonedDoc.querySelectorAll('.exclude-from-pdf');
                excludeFromPdf.forEach((div: Element) => {
                  (div as HTMLElement).style.display = 'none';
                });
              },
            });

            let imageData = canvas.toDataURL('image/png');
            let imageBytes = this.dataURLToBytes(imageData);
            imageData = '';
            const image = await pdfDoc.embedPng(imageBytes);
            imageBytes = new Uint8Array(0);

            page.drawImage(image, {
              x: 0,
              y: 0,
              width: page.getWidth(),
              height: page.getHeight(),
            });
          }

          this._progress.next({
            currentPage: totalPages,
            totalPages,
            pageTitle: `Page ${totalPages} of ${totalPages}`,
            status: 'completed',
          });

          const pdfBytes = await pdfDoc.save();

          console.log('PDF generated successfully');
          observer.next(pdfBytes);
          observer.complete();
          try {
            this.mixpanelService.stop_session_recording();
          } catch (e) {
            console.error(e);
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          this._progress.next({
            currentPage: 0,
            totalPages: 0,
            status: 'error',
            errorMessage,
          });
          console.error('PDF generation failed', error);
          observer.error(error);
        }
      })();
    });
  }

  async extractColorsFromOrgLogo(config: ReportConfig): Promise<Palette | null> {
    const imageSource = await this.getOrgLogoImageSource(config);

    if (!imageSource) {
      console.warn('No organization logo available');
      return null;
    }

    try {
      const vibrant = new Vibrant(imageSource);
      const palette = await vibrant.getPalette();
      return palette;
    } catch (error) {
      console.error('Failed to extract colors from org logo:', error);
      return null;
    }
  }

  private async getOrgLogoImageSource(config: ReportConfig): Promise<string | null> {
    // Use base64 data if available (same as PDF renderer uses)
    if (config.organizationPhoto?.base64Data) {
      return config.organizationPhoto.base64Data;
    }

    // Fallback to default logo
    return 'assets/images/app-logo-header.png';
  }

  private getPageDimensions(orientation: 'portrait' | 'landscape'): [number, number] {
    const base = { width: 595, height: 842 };
    return orientation === 'landscape' ? [base.height, base.width] : [base.width, base.height];
  }

  private generateErrorString(renderErrors?: Record<number, string[]>): string {
    if (!renderErrors) {
      return '';
    }

    let errorArray: string[] = [];
    Object.entries(renderErrors).forEach(([pageIdStr, renderErrors]) => {
      errorArray = [...errorArray, ...renderErrors];
    });
    return errorArray.join('<br />');
  }

  private dataURLToBytes(dataURL: string): Uint8Array {
    const base64 = dataURL.split(',')[1];
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes;
  }
}
